import { companyPath } from 'helper/recordPaths';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Mail } from 'lucide-react';
import AddressGeoApi from 'components/AddressGeoApi/AddressGeoApi';
import Button from 'components/crm/ui/Button';
import Chip from 'components/crm/ui/Chip';
import CrmScope from 'components/crm/ui/CrmScope';
import { FIELD_GAP_CLASS, LABEL_CLASS } from 'components/crm/ui/fieldStyles';
import Drawer from 'components/crm/ui/Drawer';
import RichSelect from 'components/crm/ui/RichSelect';
import TextField from 'components/crm/ui/TextField';
import { useToast } from 'components/crm/ui/Toast';
import { isMail } from 'components/mail/scheduleSlots';
import CompanyDirectorySearch from 'components/prospection/CompanyDirectorySearch';
import FollowedCompany, {
  CompanyCard,
} from 'components/prospection/FollowedCompany';
import PhoneField from 'components/prospection/PhoneField';
import ProspectContacts from 'components/prospection/ProspectContacts';
import {
  buildProspectPayload,
  emptyProspectForm,
  hasAddressDepartment,
  isProspectFormValid,
} from 'components/prospection/prospectForm';
import {
  emptyReferrer,
  isSourceDetailValid,
  PROSPECT_SOURCES,
  referrerErrors,
  SOCIAL_NETWORKS,
  sourceDetailFields,
} from 'helper/prospectOptions';
import { commercialsOf } from 'components/companies/companyAssignees';
import { canProspect, COMMERCIAL_ROLE } from 'helper/roles';
import { apiErrorText, isCanceledRequest } from 'helper/apiErrorText';
import UseContext from 'hooks/useContext';
import useText from 'hooks/useText';
import ApiDatabase from 'server';
import { createProspect, listTeam } from 'server/crm/prospectionApi';

// Mounted only while open, so that every opening starts from an empty form.
// `frame` ({ body, footer }) lays it out elsewhere than in its Drawer (the
// slide-over « Ajouter une entreprise », components/companies/AddCompanyPanel).
// `todayList` ({ sessionId }, the day session of /prospection) puts a prospect
// the caller takes in his call list of the day; Entreprises never passes it.
export const AddProspectForm = ({ onClose, onAdded, frame, todayList }) => {
  const { text: prospectionText } = useText('prospection');
  const { text, language } = useText('prospection.addProspect');
  const { userData } = UseContext();
  const toast = useToast();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  // Whoever prospects takes the prospect when no commercial is sent (api): the
  // commercial starts on himself, the agency manager may leave the field
  // empty. The collaborator and the recruiter, who do not prospect, may name
  // the commercial in charge, a 510 or a 500; left empty, the prospect waits
  // in the agency manager's « À attribuer » (decision of 31/10/2026).
  const role = Number(userData?.role);
  const commercial = role === COMMERCIAL_ROLE;
  const takesByDefault = canProspect(role);
  const [form, setForm] = useState(() => ({
    ...emptyProspectForm(),
    sourceNetwork: '',
    referrer: emptyReferrer(),
    commercialId: commercial ? (userData?._id ?? '') : '',
  }));
  const [commercials, setCommercials] = useState([]);
  useEffect(() => {
    listTeam().then(
      (team) => setCommercials(commercialsOf(team ?? [])),
      () => setCommercials([]),
    );
  }, []);
  // Optional field: without the list, the category stays empty and the
  // prospect can still be added.
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    let mounted = true;
    ApiDatabase.getPennyLaneCategories(
      {},
      (data) => mounted && setCategories(data?.items ?? []),
      (error) => {
        if (error?.name !== 'CanceledError') console.error(error);
      },
    );
    return () => {
      mounted = false;
    };
  }, []);
  const [nameLeft, setNameLeft] = useState(false);
  // The referrer's fields left once, whose error then shows.
  const [referrerLeft, setReferrerLeft] = useState({});
  const [busy, setBusy] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const updateReferrer = (key, value) =>
    setForm((f) => ({ ...f, referrer: { ...f.referrer, [key]: value } }));
  const leaveReferrer = (key) =>
    setReferrerLeft((left) => ({ ...left, [key]: true }));

  const pick = (picked) => {
    setCompany(picked);
    setNameLeft(false);
    setForm((f) => ({
      ...f,
      name: picked.name ?? '',
      address: picked.manual ? null : (picked.location ?? null),
    }));
  };

  const change = () => setCompany(null);

  const valid = isProspectFormValid({ company, form }) && isSourceDetailValid(form);
  const followed = company?.followed ?? null;
  // Without a commercial picked, the api gives the prospect to the caller.
  const takenByCaller = form.commercialId
    ? form.commercialId === userData?._id
    : takesByDefault;

  const submit = () => {
    if (!valid || busy) return;
    setBusy(true);
    const today = todayList && takenByCaller ? todayList : null;
    const payload = buildProspectPayload({ company, form, today });
    const fields = { ...payload.fields, ...sourceDetailFields(form) };
    createProspect({ ...payload, fields }).then(
      (result) => {
        toast(text('added'));
        onAdded?.(result);
        onClose();
      },
      (failure) => {
        setBusy(false);
        if (isCanceledRequest(failure)) return;
        toast(
          text('failed', {
            error: apiErrorText(failure, { language, text: prospectionText, group: 'errors' }),
          }),
          'error',
        );
      },
    );
  };

  const renderNetwork = () => (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-semibold text-ink">{text('source.network')}</p>
      <div className="flex flex-wrap gap-2">
        {SOCIAL_NETWORKS.map((code) => (
          <Chip
            key={code}
            selected={form.sourceNetwork === code}
            onClick={() => update('sourceNetwork', code)}
          >
            {prospectionText(`networks.${code}`)}
          </Chip>
        ))}
      </div>
    </div>
  );

  const renderReferrer = () => {
    const errors = referrerErrors(form.referrer);
    // The hint turns into an error once the e-mail is left with both empty
    // (PhoneField keeps its blur to itself).
    const reachMissing = errors.reach && referrerLeft.email;
    // Framed: the referrer is a person apart from the prospect's own fields.
    return (
      <div className="flex flex-col gap-4 rounded-xl border border-gray-200 p-4">
        <p className="text-sm font-semibold text-ink">
          {text('source.referrer.title')}
        </p>
        <TextField
          id="prospect-referrer-firstname"
          label={text('source.referrer.firstname')}
          value={form.referrer.firstname}
          onChange={(event) => updateReferrer('firstname', event.target.value)}
          onBlur={() => leaveReferrer('firstname')}
          error={
            referrerLeft.firstname && errors.firstname
              ? text('source.referrer.firstnameRequired')
              : undefined
          }
        />
        <TextField
          id="prospect-referrer-lastname"
          label={text('source.referrer.lastname')}
          value={form.referrer.lastname}
          onChange={(event) => updateReferrer('lastname', event.target.value)}
          onBlur={() => leaveReferrer('lastname')}
          error={
            referrerLeft.lastname && errors.lastname
              ? text('source.referrer.lastnameRequired')
              : undefined
          }
        />
        <TextField
          id="prospect-referrer-email"
          type="email"
          label={text('source.referrer.email')}
          icon={Mail}
          value={form.referrer.email}
          onChange={(event) => updateReferrer('email', event.target.value)}
          onBlur={() => leaveReferrer('email')}
          error={errors.email ? text('email.invalid') : undefined}
        />
        <div className={`flex flex-col ${FIELD_GAP_CLASS}`}>
          <PhoneField
            id="prospect-referrer-phone"
            label={text('source.referrer.phone')}
            placeholder={text('phone.placeholder')}
            value={form.referrer.phone}
            onChange={(value) => updateReferrer('phone', value)}
          />
          <p
            className={`-mt-1 text-xs leading-4 ${
              reachMissing ? 'text-danger' : 'text-faint'
            }`}
          >
            {text('source.referrer.reachRequired')}
          </p>
        </div>
      </div>
    );
  };

  const renderForm = () => (
    <>
      {company.manual ? (
        <TextField
          id="prospect-name"
          label={text('company.nameLabel')}
          icon={Building2}
          value={form.name}
          onChange={(event) => update('name', event.target.value)}
          onBlur={() => setNameLeft(true)}
          error={
            nameLeft && !form.name.trim()
              ? text('company.nameRequired')
              : undefined
          }
        />
      ) : (
        <CompanyCard company={company} onChange={change} />
      )}
      <div className={`flex flex-col ${FIELD_GAP_CLASS}`}>
        <p className={LABEL_CLASS}>{text('address.label')}</p>
        <AddressGeoApi
          page="createCompany"
          addressValue={form.address}
          addressGeoValue={(address) => update('address', address)}
        />
        {form.address?.street && !hasAddressDepartment(form.address) ? (
          <p className="-mt-1 text-xs leading-4 text-danger">
            {text('address.departmentRequired')}
          </p>
        ) : (
          !company.manual && (
            <p className="-mt-1 text-xs leading-4 text-faint">
              {text('address.hint')}
            </p>
          )
        )}
      </div>
      <PhoneField
        id="prospect-phone"
        label={text('phone.label')}
        placeholder={text('phone.placeholder')}
        value={form.phone}
        required
        onChange={(value) => update('phone', value)}
      />
      <TextField
        id="prospect-email"
        type="email"
        label={text('email.label')}
        icon={Mail}
        placeholder={text('email.placeholder')}
        value={form.email}
        onChange={(event) => update('email', event.target.value)}
        error={form.email.trim() && !isMail(form.email) ? text('email.invalid') : undefined}
      />
      <ProspectContacts
        contacts={form.contacts}
        onChange={(contacts) => update('contacts', contacts)}
      />
      <RichSelect
        id="prospect-category"
        label={text('category')}
        value={form.pennylaneCategoryId}
        options={[
          { value: '', label: text('noCategory') },
          ...categories.map((category) => ({
            value: category.id,
            label: category.label,
          })),
        ]}
        onChange={(value) => update('pennylaneCategoryId', value)}
      />
      <div className="flex flex-col gap-3">
        <p className="text-sm font-semibold text-ink">{text('source.label')}</p>
        <div className="flex flex-wrap gap-2">
          {PROSPECT_SOURCES.map((code) => (
            <Chip
              key={code}
              selected={form.source === code}
              onClick={() => update('source', code)}
            >
              {prospectionText(`sources.${code}`)}
            </Chip>
          ))}
        </div>
      </div>
      {form.source === 'social' && renderNetwork()}
      {form.source === 'referral' && renderReferrer()}
      <RichSelect
        id="prospect-commercial"
        label={text(commercial ? 'commercial.label' : 'commercial.labelOptional')}
        placeholder={text('commercial.placeholder')}
        hint={
          takesByDefault
            ? text(commercial ? 'commercial.hintSelf' : 'commercial.hintEmptySelf')
            : 'Sans commercial, le prospect attend dans « À attribuer », où le responsable d’agence le confie.'
        }
        value={form.commercialId}
        options={commercials.map((person) => ({
          value: person.id,
          label:
            person.id === userData?._id
              ? text('commercial.me', { name: `${person.firstname} ${person.lastname}`.trim() })
              : `${person.firstname} ${person.lastname}`.trim(),
        }))}
        onChange={(value) => update('commercialId', value)}
      />
    </>
  );

  const renderBody = () => {
    if (!company) {
      return <CompanyDirectorySearch onPick={pick} autoFocus />;
    }
    if (followed) {
      return (
        <FollowedCompany
          company={company}
          currentUserId={userData?._id}
          onChange={change}
        />
      );
    }
    return renderForm();
  };

  const footer = (
    <CrmScope className="flex flex-1 items-center justify-end gap-3">
      <Button variant="secondary" onClick={onClose}>
        {text('cancel')}
      </Button>
      {followed ? (
        <Button onClick={() => navigate(companyPath(followed.companyId))}>
          {text('followed.open')}
        </Button>
      ) : (
        <Button disabled={!valid || busy} onClick={submit}>
          {busy ? text('submitting') : text('submit')}
        </Button>
      )}
    </CrmScope>
  );
  const body = <CrmScope className="flex flex-col gap-6">{renderBody()}</CrmScope>;
  if (frame) return frame({ body, footer });
  return (
    <Drawer
      open
      onClose={onClose}
      // Once a company is chosen, an Escape or a stray click would lose it all.
      dismissible={!company}
      title={text('title')}
      description={<CrmScope>{text('subtitle')}</CrmScope>}
      footer={footer}
    >
      {body}
    </Drawer>
  );
};

const AddProspectDrawer = ({ open, onClose, onAdded, todayList }) =>
  open ? (
    <AddProspectForm
      onClose={onClose}
      onAdded={onAdded}
      todayList={todayList}
    />
  ) : null;

export default AddProspectDrawer;
